import React, { Component, useRef } from 'react';

import { isEqual, omitBy } from 'lodash-es';
import qs, { stringify } from 'qs';
import { connect } from 'react-redux';
import { Redirect } from 'react-resource-router';
import { compose } from 'redux';

import { ContextualAnalyticsData, SCREEN } from '@atlassian/analytics-bridge';
import { AtlBrowserStorageSession } from '@atlassian/browser-storage-controls';

import {
  getEndPage,
  getPageLength,
  getStartPage,
  getHasNextPage,
  loadCommits,
  updateCurrentPage,
  updateStartPage,
  pageUnload,
  getPaginationUrls,
  PaginationUrls,
} from 'src/redux/commit-list';
import { dismissFlag } from 'src/redux/flags';
import withLocation from 'src/router/utils/with-location';
import { RouteProps } from 'src/sections/repository/types';
import { getCurrentRepositoryMainBranchName } from 'src/selectors/repository-selectors';
import { BucketState } from 'src/types/state';
import urls from 'src/urls/commit';
import { safeEncodePath, safeDecodePath, safeDecode } from 'src/utils/path';

import repositoryUrls from '../../urls';

import Page from './components/page';
import { Filter as FilterParams } from './types';

type Props = RouteProps<{
  refName: string;
}> & {
  startPage: number;
  endPage: number;
  hasNextPage: boolean;
  loadCommits: typeof loadCommits;
  pageLength: number;
  updateCurrentPage: typeof updateCurrentPage;
  updateStartPage: typeof updateStartPage;
  pageUnload: typeof pageUnload;
  dismissFlag: typeof dismissFlag;
  paginationUrls: PaginationUrls | {};
  mainBranchName: string | undefined;
  onClickShowAll?: () => void;
};

export class CommitListRoute extends Component<Props> {
  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    hasNextPage: false,
  };

  componentDidMount() {
    const { page } = this.parseQuery();

    this.props.updateStartPage(page);
    this.props.updateCurrentPage(page);
    this.loadCommits();
  }

  componentDidUpdate(prevProps: Props) {
    const { repositoryFullSlug } = this.props.match.params;
    const { repositoryFullSlug: prevRepositoryFullSlug } =
      prevProps.match.params;

    const refName = this.getRefName();
    const prevRefName = this.getRefName(prevProps);

    const { page, search } = this.parseQuery();
    const { page: prevPage, search: prevSearch } = this.parseQuery(prevProps);

    const sourceOfCommitsChanged =
      repositoryFullSlug !== prevRepositoryFullSlug || refName !== prevRefName;

    const searchChanged = search !== prevSearch;

    const filterChanged =
      sourceOfCommitsChanged || page !== prevPage || searchChanged;

    if (filterChanged) {
      this.props.dismissFlag('commits-list-updated');
      const { startPage, endPage, paginationUrls } = this.props;

      this.props.updateCurrentPage(sourceOfCommitsChanged ? prevPage : page);

      if (sourceOfCommitsChanged) {
        this.loadCommits(true);
      } else if (page < startPage) {
        // @ts-ignore TODO: fix noImplicitAny error here
        const previousUrl = paginationUrls[prevPage]
          ? (paginationUrls as any)[prevPage].previousUrl
          : undefined;
        this.loadCommits(false, previousUrl);
      } else if (page > endPage) {
        // @ts-ignore TODO: fix noImplicitAny error here
        const nextUrl = paginationUrls[prevPage]
          ? (paginationUrls as any)[prevPage].nextUrl
          : undefined;
        this.loadCommits(false, nextUrl);
      } else if (searchChanged) {
        this.loadCommits(true);
      }
    }
  }

  componentWillUnmount() {
    this.props.pageUnload();
    this.props.dismissFlag('commits-list-updated');
  }

  getRefName = (props: Props = this.props) => {
    const { refName } = props.match.params;
    return refName ? safeDecode(refName) : undefined;
  };

  buildUrl = (page: number) => {
    const { search: query, pathname } = this.props.location;

    const oldQuery = qs.parse(query, {
      ignoreQueryPrefix: true,
    });

    const newQuery = qs.stringify(
      {
        ...oldQuery,
        page: page > 1 ? page : undefined,
      },
      { addQueryPrefix: true }
    );
    // the pathname comes encoded here, but because <Link />
    // applies `decodeURI` to passed paths, we need to double-encode it
    // using `safeEncodePath`. For more info see `src/utils/path.ts`
    return `${safeEncodePath(safeDecodePath(pathname))}${newQuery}`;
  };

  onFilterChange = (params: Partial<FilterParams>) => {
    const {
      history,
      match: {
        params: { repositoryFullSlug },
      },
    } = this.props;

    const fallbackParams: FilterParams = {
      search: '',
      page: 1,
    };

    // @ts-ignore TODO: fix noImplicitAny error here
    const urlParams = omitBy(params, (v, k) => isEqual(fallbackParams[k], v));
    const query = stringify(urlParams, { skipNulls: true, indices: false });
    history.push(`${urls.ui.commits(repositoryFullSlug)}/?${query}`);
  };

  getNextPageUrl = () => {
    if (!this.props.hasNextPage) {
      return undefined;
    }

    const { page } = this.parseQuery();
    return this.buildUrl(+page + 1);
  };

  getPreviousPageUrl = () => {
    const { page } = this.parseQuery();
    return page > 1 ? this.buildUrl(page - 1) : undefined;
  };

  loadCommits = (isFiltering = false, url?: string) => {
    const { pageLength, match } = this.props;
    const { repositoryFullSlug } = match.params;
    const refName = this.getRefName();
    const { page, search, ctx } = this.parseQuery(undefined, url);

    this.props.loadCommits({
      page,
      pageLength,
      refName,
      repositoryFullSlug,
      search,
      isFiltering,
      ctx,
    });
  };

  parseQuery = (props?: Props, url?: string) => {
    const query = url || (props || this.props).location.search;
    const {
      page = '1',
      search = '',
      ctx = '',
    } = qs.parse(query, {
      ignoreQueryPrefix: true,
    });

    return { page: +page, search: search as string, ctx: ctx as string };
  };

  render() {
    const { mainBranchName } = this.props;
    const { repositoryFullSlug } = this.props.match.params;
    const refName = this.getRefName();

    return (
      <ContextualAnalyticsData sourceName="commits" sourceType={SCREEN}>
        <Page
          nextUrl={this.getNextPageUrl()}
          previousUrl={this.getPreviousPageUrl()}
          refName={refName}
          query={this.parseQuery()}
          onFilterChange={this.onFilterChange}
          onClickShowAll={this.props.onClickShowAll}
          repositoryFullSlug={repositoryFullSlug}
          isEmpty={!mainBranchName}
        />
      </ContextualAnalyticsData>
    );
  }
}

/**
 * We want to default the commit list page to show commits for the main branch
 * instead of all branches, for performance reasons. The way the page controls
 * this is via the URL. Once we have loaded the main branch (which comes from
 * redux), we redirect to a URL with that main branch name. Clicking the 'Show
 * all' button also changes the URL, so in order for that to work at all we
 * have to remove the redirect once that link is clicked the first time.
 */
const CommitListWithDefaultRef = (props: Props) => {
  const STORAGE_KEY = 'bbc-commit-list-main-branch';
  const { refName, repositoryFullSlug } = props.match.params;
  const hasClickedShowAllRef = useRef(false);
  const query = props.location.search;
  const { search = '' } = qs.parse(query, { ignoreQueryPrefix: true });

  if (
    // if the user has clicked "Show all" during this render
    hasClickedShowAllRef.current ||
    // if the user has clicked the link at all during this session
    AtlBrowserStorageSession.getItem(STORAGE_KEY) === 'true' ||
    // if the user enters a search term, we search all branches and shouldn't redirect
    search !== ''
  ) {
    return <CommitListRoute {...props} />;
  }

  // If we have a main branch and no ref name, default to showing the main
  // branch instead of all branches
  if (props.mainBranchName && !refName) {
    const url = repositoryUrls.ui.commits(repositoryFullSlug, {
      kind: 'branch',
      refName: props.mainBranchName,
    });
    return <Redirect to={url} />;
  }

  const onClickShowAll = () => {
    try {
      // Save the user's preference for this session. Note this is global, not
      // per repository
      AtlBrowserStorageSession.setStrictlyNecessaryItem(STORAGE_KEY, 'true');
    } catch {
      // no session storage available, fall back to ref so the link works
      hasClickedShowAllRef.current = true;
    }
  };

  return <CommitListRoute {...props} onClickShowAll={onClickShowAll} />;
};

const mapStateToProps = (state: BucketState) => ({
  hasNextPage: getHasNextPage(state),
  pageLength: getPageLength(state),
  endPage: getEndPage(state),
  startPage: getStartPage(state),
  paginationUrls: getPaginationUrls(state),
  mainBranchName: getCurrentRepositoryMainBranchName(state),
});
const mapDispatchToProps = {
  loadCommits,
  updateCurrentPage,
  updateStartPage,
  pageUnload,
  dismissFlag,
};

export default compose(
  withLocation,
  connect(mapStateToProps, mapDispatchToProps)
)(CommitListWithDefaultRef);
